<template>
  <div class="table-container">
    <div class="header">
      <h2>Gestion des Fournisseurs</h2>
      <div class="search-box">
        <input 
          v-model="searchTerm" 
          type="text" 
          placeholder="Rechercher..." 
          class="search-input"
        />
      </div>
    </div>

    <div class="table-wrapper">
      <table class="data-table" v-if="!loading">
        <thead>
          <tr>
            <th @click="sortBy('id')" class="sortable">
              ID
              <span v-if="sortColumn === 'id'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('name')" class="sortable">
              Nom du Fournisseur
              <span v-if="sortColumn === 'name'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('status')" class="sortable">
              Statut
              <span v-if="sortColumn === 'status'">{{ sortDirection === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th>
              Courtiers
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filteredData" :key="item.id">
            <td>{{ item.id }}</td>
            <td class="supplier-name">{{ item.name }}</td>
            <td>
              <span class="status-badge en-attente">
                {{ item.status.replace('_', ' ') }}
              </span>
            </td>
            <td>
              <div class="courtiers-list">
                <span 
                  v-for="courtier in item.courtiers" 
                  :key="courtier.id"
                  class="courtier-tag"
                >
                  {{ courtier.name }}
                </span>
              </div>
            </td>
            <td>
              <button @click="showAcceptModal(item)" class="btn btn-accept">
                <i class="icon">✓</i>
                Accepter
              </button>
              <button @click="showRefuseModal(item)" class="btn btn-refuse">
                <i class="icon">✕</i>
                Refuser
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="loading" class="loading">
        <p>Chargement des données...</p>
      </div>

      <div v-if="!loading && filteredData.length === 0" class="no-data">
        <p>Aucune donnée trouvée</p>
      </div>

      <div v-if="error" class="error">
        <p>Erreur lors du chargement: {{ error }}</p>
        <button @click="fetchData" class="btn btn-retry">Réessayer</button>
      </div>
    </div>

    <div class="footer">
      <p>Total: {{ filteredData.length }} fournisseurs</p>
    </div>

    <!-- Accept Confirmation Modal -->
    <div v-if="showAcceptConfirm" class="modal-overlay" @click="hideAcceptModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Confirmer l'acceptation</h3>
          <button @click="hideAcceptModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir accepter le fournisseur <strong>{{ selectedItem?.name }}</strong> ?</p>
          <div class="courtiers-preview">
            <p><strong>Courtiers associés:</strong></p>
            <div class="courtiers-list">
              <span 
                v-for="courtier in selectedItem?.courtiers" 
                :key="courtier.id"
                class="courtier-tag"
              >
                {{ courtier.name }}
              </span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="hideAcceptModal" class="btn btn-cancel">Annuler</button>
          <button @click="confirmAccept" class="btn btn-accept" :disabled="accepting">
            <span v-if="accepting">Traitement...</span>
            <span v-else>Accepter</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Refuse Confirmation Modal -->
    <div v-if="showRefuseConfirm" class="modal-overlay" @click="hideRefuseModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>Confirmer le refus</h3>
          <button @click="hideRefuseModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir refuser le fournisseur <strong>{{ selectedItem?.name }}</strong> ?</p>
          <div class="courtiers-preview">
            <p><strong>Courtiers associés:</strong></p>
            <div class="courtiers-list">
              <span 
                v-for="courtier in selectedItem?.courtiers" 
                :key="courtier.id"
                class="courtier-tag"
              >
                {{ courtier.name }}
              </span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="hideRefuseModal" class="btn btn-cancel">Annuler</button>
          <button @click="confirmRefuse" class="btn btn-refuse" :disabled="refusing">
            <span v-if="refusing">Traitement...</span>
            <span v-else>Refuser</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Success/Error Message Modal -->
    <div v-if="showMessage" class="modal-overlay" @click="hideMessage">
      <div class="modal message-modal" @click.stop>
        <div class="modal-header" :class="messageType">
          <h3>{{ messageType === 'success' ? 'Succès' : 'Erreur' }}</h3>
          <button @click="hideMessage" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <p>{{ messageText }}</p>
        </div>
        <div class="modal-footer">
          <button @click="hideMessage" class="btn btn-primary">OK</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UpdateStatus',
  data() {
    return {
      searchTerm: '',
      sortColumn: '',
      sortDirection: 'asc',
      tableData: [],
      loading: false,
      error: null,
      // Modal states
      showAcceptConfirm: false,
      showRefuseConfirm: false,
      showMessage: false,
      selectedItem: null,
      accepting: false,
      refusing: false,
      messageText: '',
      messageType: 'success' // 'success' or 'error'
    }
  },
  mounted() {
    this.fetchData();
  },
  computed: {
    filteredData() {
      let filtered = this.tableData;

      // Apply search filter
      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase();
        filtered = filtered.filter(item => 
          item.name.toLowerCase().includes(search) ||
          item.status.toLowerCase().includes(search) ||
          item.courtiers.some(courtier => 
            courtier.name.toLowerCase().includes(search)
          )
        );
      }

      // Apply sorting
      if (this.sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          let aValue = a[this.sortColumn];
          let bValue = b[this.sortColumn];

          if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
          }

          if (aValue < bValue) {
            return this.sortDirection === 'asc' ? -1 : 1;
          }
          if (aValue > bValue) {
            return this.sortDirection === 'asc' ? 1 : -1;
          }
          return 0;
        });
      }

      return filtered;
    }
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await fetch(`${process.env.VUE_APP_URL_API_CLIENT}get_en_attente_fournisseur_updates`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        this.tableData = data;
        
      } catch (error) {
        console.error('Error fetching data:', error);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    },
    // Modal methods
    showAcceptModal(item) {
      this.selectedItem = item;
      this.showAcceptConfirm = true;
    },
    hideAcceptModal() {
      this.showAcceptConfirm = false;
      this.selectedItem = null;
      this.accepting = false;
    },
    showRefuseModal(item) {
      this.selectedItem = item;
      this.showRefuseConfirm = true;
    },
    hideRefuseModal() {
      this.showRefuseConfirm = false;
      this.selectedItem = null;
      this.refusing = false;
    },
    showSuccessMessage(message) {
      this.messageText = message;
      this.messageType = 'success';
      this.showMessage = true;
    },
    showErrorMessage(message) {
      this.messageText = message;
      this.messageType = 'error';
      this.showMessage = true;
    },
    hideMessage() {
      this.showMessage = false;
      this.messageText = '';
    },
    async confirmAccept() {
      if (!this.selectedItem) return;
      
      this.accepting = true;
      const supplierName = this.selectedItem.name; // Store name before removal
      
      try {
        const response = await fetch(`${process.env.VUE_APP_URL_API_CLIENT}accepte_fournisseur_update/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: this.selectedItem.id
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erreur lors de l\'acceptation');
        }

        const result = await response.json();
        console.log('Success:', result.message);

        // Remove item from list when accepted
        const index = this.tableData.findIndex(data => data.id === this.selectedItem.id);
        if (index > -1) {
          this.tableData.splice(index, 1);
        }

        this.hideAcceptModal();
        this.showSuccessMessage(`Fournisseur "${supplierName}" accepté avec succès!`);
        
      } catch (error) {
        console.error('Error accepting item:', error);
        this.showErrorMessage(`Erreur lors de l'acceptation: ${error.message}`);
      } finally {
        this.accepting = false;
      }
    },
    async confirmRefuse() {
      if (!this.selectedItem) return;
      
      this.refusing = true;
      const supplierName = this.selectedItem.name; // Store name before removal
      
      try {
        const response = await fetch(`${process.env.VUE_APP_URL_API_CLIENT}refuse_fournisseur_update/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: this.selectedItem.id
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Erreur lors du refus');
        }

        const result = await response.json();
        console.log('Success:', result.message);

        // Remove item from list when refused
        const index = this.tableData.findIndex(item => item.id === this.selectedItem.id);
        if (index > -1) {
          this.tableData.splice(index, 1);
        }

        this.hideRefuseModal();
        this.showSuccessMessage(`Fournisseur "${supplierName}" refusé avec succès!`);
        
      } catch (error) {
        console.error('Error refusing item:', error);
        this.showErrorMessage(`Erreur lors du refus: ${error.message}`);
      } finally {
        this.refusing = false;
      }
    }
  }
}
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin: 24px;
  border: 1px solid #e5e7eb;
}

.header {
  background: #4f46e5;
  color: white;
  padding: 24px 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.search-box {
  display: flex;
  align-items: center;
}

.search-input {
  padding: 10px 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  font-size: 14px;
  width: 280px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  transition: all 0.2s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.search-input:focus {
  outline: none;
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.2);
}

.table-wrapper {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  background: #f9fafb;
  padding: 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  font-size: 14px;
}

.data-table th.sortable {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.data-table th.sortable:hover {
  background: #f3f4f6;
}

.data-table td {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
  color: #374151;
}

.data-table tr {
  transition: background-color 0.2s ease;
}

.data-table tr:hover {
  background: #f8fafc;
}

.status-badge {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  display: inline-block;
}

.supplier-name {
  font-weight: 600;
  color: #1f2937;
}

.courtiers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 300px;
}

.courtier-tag {
  display: inline-block;
  padding: 4px 8px;
  background: #e0e7ff;
  color: #3730a3;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.loading, .error {
  text-align: center;
  padding: 48px 20px;
  color: #6b7280;
}

.btn-retry {
  background: #4f46e5;
  color: white;
  border-color: #4f46e5;
  margin-top: 12px;
}

.btn-retry:hover {
  background: #4338ca;
  border-color: #4338ca;
}

.status-badge.en-attente {
  background: #fef3c7;
  color: #92400e;
}

.btn {
  padding: 8px 16px;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  margin-right: 8px;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn .icon {
  font-size: 12px;
}

.btn-accept {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.btn-accept:hover {
  background: #059669;
  border-color: #059669;
}

.btn-refuse {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

.btn-refuse:hover {
  background: #dc2626;
  border-color: #dc2626;
}

.no-data {
  text-align: center;
  padding: 48px 20px;
  color: #6b7280;
}

.footer {
  background: #f9fafb;
  padding: 16px 32px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 14px;
}

.footer p {
  margin: 0;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  min-width: 400px;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9fafb;
}

.modal-header.success {
  background: #ecfdf5;
  border-bottom-color: #d1fae5;
}

.modal-header.error {
  background: #fef2f2;
  border-bottom-color: #fecaca;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
}

.modal-body p {
  margin: 0 0 16px 0;
  color: #374151;
  line-height: 1.6;
}

.courtiers-preview {
  background: #f8fafc;
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
}

.courtiers-preview p {
  margin: 0 0 8px 0;
  font-weight: 600;
  color: #1f2937;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #f9fafb;
}

.btn-cancel {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

.btn-cancel:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.btn-primary {
  background: #4f46e5;
  color: white;
  border-color: #4f46e5;
}

.btn-primary:hover {
  background: #4338ca;
  border-color: #4338ca;
}

.message-modal {
  min-width: 300px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn:disabled:hover {
  transform: none;
}

@media (max-width: 640px) {
  .modal {
    min-width: 90vw;
    margin: 20px;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .modal-footer .btn {
    width: 100%;
    margin-right: 0;
  }
}
</style>